/**
 * 組織レポート v3 の表示データ組み立て。
 *
 * 正典: apps/web/docs/新方針20260821/仕様_GB組織レポートの生成ロジック.html
 *       apps/web/docs/新方針20260821/モック_GB組織レポート_v3 (3).html
 *
 * 会社メンバーの gb74 個人スコアを受け取り、画面が必要とするものを一式にして返す。
 * 算出は orgScoreV3 / orgTypeV3、文言は orgTextsV3 に任せ、ここでは組み立てだけを行う。
 *
 * 【現行 buildOrgReportFromResults.ts との関係】
 *  現行は旧路線（b7・モバイルの /embed/report/org）が使うので温存する。
 */
import type { AxisKey, Gb74Scores } from './gb74Report';
import { buildOrgReportFromResults, type OrgMemberResult } from './buildOrgReportFromResults';
import {
  buildOrgScoresV3,
  deriveIndividualV3,
  type IndividualV3,
  type OrgScoresV3,
} from './orgScoreV3';
import { classifyOrgTypeV3, type AxisScores, type OrgTypeResultV3 } from './orgTypeV3';
import { judgeConsultDeptV3, type ConsultDeptResultV3 } from './orgConsultV3';
import { buildOrgCohesionV3, type OrgCohesionV3 } from './orgCohesionV3';
import { buildOrgImprovementV3, type OrgImprovementV3 } from './orgImprovementV3';
import { buildOrgDistributionV3, type OrgDistributionV3 } from './orgDistributionV3';
import { BIG5_FACTORS, getFactorScore } from './gb74Report';
import {
  axisMeansOf,
  getAxisTiltNote,
  getConnVarText,
  getOrgCatch,
  getOrgShapeText,
  getOrgTypeText,
  getScoreText,
  type OrgTextEntry,
} from './orgTextsV3';
import { CONNECTION_VAR_KEYS, toGrade, toScore100, type ConnectionVarsV3 } from './orgScoreV3';

/** 入力: 1メンバー分。 */
export interface OrgMemberV3 {
  /** 表示には使わない（社員の分布では氏名を出さない）。デバッグ用 */
  name?: string;
  scores: Gb74Scores;
  /** 部署名（前バージョンの説明文取得に渡す） */
  departmentName?: string | null;
}

/** 散布図の1点。 */
export interface ScatterPointV3 {
  /** 右脳 − 左脳 =(C+D)−(A+B)。範囲 −24〜+24 */
  x: number;
  /** 上部 − 下部 =(A+D)−(B+C)。範囲 −24〜+24 */
  y: number;
  /** マーカー（小さなひし形）を描くための4軸 */
  axis: AxisScores;
}

/** 中心付近とみなす範囲（各軸差の絶対値がこれ以下）。DB04の判定入力。 */
export const SCATTER_CENTER_MAX = 3;

export interface ScatterSummaryV3 {
  points: ScatterPointV3[];
  /** 左上(A寄り)・右上(D寄り)・右下(C寄り)・左下(B寄り)・中央 の人数 */
  counts: { A: number; B: number; C: number; D: number; center: number };
}

export interface OrgReportV3Data {
  companyName: string;
  /** 「2026年8月／22人中22人が回答」の材料 */
  period: string;
  answered: number;
  total: number;
  /** 3スコア・GBスコア・グレード */
  scores: OrgScoresV3;
  /** 4軸の組織平均（0〜12・合計24） */
  axisMeans: Record<AxisKey, number>;
  /** タイプコードと型 */
  type: OrgTypeResultV3;
  /** 社員の分布 */
  scatter: ScatterSummaryV3;
  /**
   * メンバー一覧（前バージョンの「組織メンバーの各タイプ」を引き継ぐ）。
   * 散布図では氏名を出さないが、このセクションは前バージョン同様に氏名を出す。
   */
  members: Array<{ name: string; axis: Record<AxisKey, number> }>;
  /** つながりの構造（部署別）。相談データが無ければ空 */
  consult: ConsultDeptResultV3[];
  /** 組織の噛み合わせ（コミュニケーション負荷・DB03） */
  cohesion: OrgCohesionV3;
  /** 社員の分布の形（DB04） */
  distribution: OrgDistributionV3;
  /**
   * 12原型の示唆3件（3-2「あわせて考えたいこと」で使う）。
   * 社員の分布(1-3)はDB04へ置き換えたが、採用・配置の観点を持つこの示唆は
   * DB07に無い角度なので、ここで拾い続ける。
   */
  archetypeActions: Array<{ title: string; body: string; thisMonth: string }>;
  /** 評価を上げるために（PI-1・DB07） */
  improvement: OrgImprovementV3;
  /**
   * 社員の分布の説明文。
   * 前バージョン(b7)と同じ手順で取得する（2026-08-22 指示）。
   * buildOrgReportFromResults の distributionLead をそのまま使うことで、
   * 12原型の確定文言 →（DBパターンがあれば tdesc13/14）という経路を共有する。
   */
  distributionLead: string;
  /** 文言 */
  texts: {
    /** DB01 冒頭のキャッチ */
    catch?: OrgTextEntry;
    /** DB14 タイプコードの説明 */
    typeCode?: OrgTextEntry;
    /** DB14 型の説明 */
    shape?: OrgTextEntry;
    /** DB02 3スコアの説明（最低スコアだけ詳しく出す。02-RULE-1つ） */
    score: Record<'wellbeing' | 'connection' | 'retention', OrgTextEntry | undefined>;
    /** DB06 つながり4変数の内訳 */
    connVars: Array<{ key: keyof ConnectionVarsV3; value: number; text?: OrgTextEntry }>;
    /** DB10 傾きが偶然の範囲であることの注記 */
    tiltNote?: OrgTextEntry;
  };
}

/**
 * 緩衝力の素 ＝ 配慮性・感情制御の平均（1.0〜5.0）。
 * 噛み合わせの修飾句（緩衝が高い/低い）に使う。
 */
function bufferOf(scores: Gb74Scores): number | null {
  const vals = ['consideration', 'emotionControl']
    .map((k) => BIG5_FACTORS.find((f) => f.key === k))
    .filter((f): f is NonNullable<typeof f> => Boolean(f))
    .map((f) => getFactorScore(scores, f))
    .filter((v) => v > 0);
  return vals.length === 0 ? null : vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** 4軸スコアを取り出す（thinkingType は 0〜12 の個数）。 */
function axisOf(scores: Gb74Scores): AxisScores {
  const tt = scores.thinkingType ?? {};
  return {
    A: Number(tt.A) || 0,
    B: Number(tt.B) || 0,
    C: Number(tt.C) || 0,
    D: Number(tt.D) || 0,
  };
}

/**
 * 社員の分布（仕様書§05）。
 * 横軸 x =（C＋D）−（A＋B）  右脳 − 左脳
 * 縦軸 y =（A＋D）−（B＋C）  上部 − 下部
 * GB人財のレーダーと軸の配置が揃うようにしてある。
 */
export function toScatterPoint(axis: AxisScores): ScatterPointV3 {
  return {
    x: axis.C + axis.D - (axis.A + axis.B),
    y: axis.A + axis.D - (axis.B + axis.C),
    axis,
  };
}

function summarizeScatter(members: AxisScores[]): ScatterSummaryV3 {
  const points = members.map(toScatterPoint);
  const counts = { A: 0, B: 0, C: 0, D: 0, center: 0 };
  points.forEach((p) => {
    if (Math.abs(p.x) <= SCATTER_CENTER_MAX && Math.abs(p.y) <= SCATTER_CENTER_MAX) {
      counts.center += 1;
      return;
    }
    // 左上=A / 右上=D / 右下=C / 左下=B
    if (p.x < 0) counts[p.y >= 0 ? 'A' : 'B'] += 1;
    else counts[p.y >= 0 ? 'D' : 'C'] += 1;
  });
  return { points, counts };
}

/** つながりの構造の入力（既存の /company/consult-network から渡す）。 */
export interface ConsultDeptInput {
  dept: string;
  members: Array<{ id: string; name: string }>;
  edges: Array<{ from: string; to: string }>;
}

export interface BuildOrgV3Options {
  companyName: string;
  /** 「2026年8月」など */
  period: string;
  /** 在籍者数（未指定なら回答者数と同じ扱い） */
  total?: number;
  /** 回答が集まりきっていないか（DB01の平坦型の枝に使う） */
  lowResponse?: boolean;
  /** つながりの構造の元データ。無ければそのセクションは出さない */
  consultDepts?: ConsultDeptInput[];
  /** 判定を行う最小人数。既定は7名。下限を下げた場合の見え方を確認するのに使う */
  consultMinJudge?: number;
}

/** メンバーの結果から画面データ一式を組み立てる。 */
export function buildOrgReportV3(members: OrgMemberV3[], opts: BuildOrgV3Options): OrgReportV3Data {
  const individuals: IndividualV3[] = members.map((m) => deriveIndividualV3(m.scores));
  const axisList: AxisScores[] = members.map((m) => axisOf(m.scores));

  const scores = buildOrgScoresV3(individuals);
  const axisMeans = axisMeansOf(axisList);
  const type = classifyOrgTypeV3(axisList);
  const scatter = summarizeScatter(axisList);

  // 変数ごとのグレードは3スコアと同じ閾値で決める（DB06の判定入力）。
  // DBは中央値の生値で A 4.20以上／B 3.40〜4.19／C 2.60〜3.39／D 2.59以下 と書くが、
  // これは 0〜100 換算の 80/60/40 と同じ境界なので、共通の変換をそのまま使う。
  const connVars = CONNECTION_VAR_KEYS.map((key) => {
    const value = scores.connection.vars[key];
    return { key, value, text: getConnVarText(key, toGrade(toScore100(value))) };
  });

  // 噛み合わせの分極は、組織タイプの判定（二極型かどうか）に合わせる。
  // 別々に判定すると「二極型なのに分極が低い」が同じレポートに並ぶ。
  const cohesion = buildOrgCohesionV3(
    members.map((m) => ({ axis: axisOf(m.scores), buffer: bufferOf(m.scores) })),
    type.shape
  );
  // 回答者が多い部署から並べる（前バージョンと同じ）
  const consult = (opts.consultDepts ?? [])
    .map((d) => judgeConsultDeptV3(d.dept, d.members, d.edges, opts.consultMinJudge))
    .sort((a, b) => b.respondents - a.respondents);

  // 12原型の示唆（採用・配置の観点）を取り出す。取れなくてもレポートは出す。
  let archetypeActions: Array<{ title: string; body: string; thisMonth: string }> = [];

  // 社員の分布の説明文は前バージョンと同じ経路で取る。
  // 算出値は v3 のものを使い、文言だけを既存ビルダーから借りる。
  let distributionLead = '';
  try {
    const legacyMembers: OrgMemberResult[] = members.map((m) => ({
      scores: m.scores,
      departmentName: m.departmentName ?? null,
    }));
    const legacy = buildOrgReportFromResults(legacyMembers, {
      companyName: opts.companyName,
      applyHerrmannLoad: true,
    });
    distributionLead = legacy.distributionLead ?? '';
    archetypeActions = (legacy.improvements ?? [])
      .slice(0, 3)
      .map((a) => ({ title: a.title, body: a.reason, thisMonth: a.action }));
  } catch {
    distributionLead = ''; // 取れなくても他のセクションは出す
  }

  return {
    companyName: opts.companyName,
    period: opts.period,
    distributionLead,
    answered: members.length,
    total: opts.total ?? members.length,
    scores,
    axisMeans,
    type,
    scatter,
    members: members.map((m, i) => ({
      name: m.name || `メンバー${i + 1}`,
      axis: axisOf(m.scores),
    })),
    cohesion,
    distribution: buildOrgDistributionV3(type.shapeDetail),
    archetypeActions,
    consult,
    improvement: buildOrgImprovementV3({ scores, consult, cohesion }),
    texts: {
      catch: getOrgCatch({
        // キャッチも型・分布と同じ象限分布から出す（食い違いを起こさないため）
        rates: type.shapeDetail.rates,
        directionless: type.shape === '中心集中型' || type.shape === '分散型',
        polarHigh: type.shape === '二極型',
        canJudgePolar: type.shape !== null,
        lowResponse: opts.lowResponse,
      }),
      typeCode: getOrgTypeText(type.code),
      shape: getOrgShapeText(type.shape),
      score: {
        wellbeing: getScoreText('wellbeing', scores.wellbeing.grade),
        connection: getScoreText('connection', scores.connection.grade),
        retention: getScoreText('retention', scores.retention.grade),
      },
      connVars,
      tiltNote: getAxisTiltNote(axisMeans, members.length),
    },
  };
}
